// The actions at the bar's right (2026-10-14): Puzzle, when the reader's
// next move was a mistake, sets the position up to solve; Main line, at any
// position once the opening is complete, on the game or on a line of the
// reader's own, plays the game's own book moves from the position to where
// the game left the book (the opening as the game played it, 2026-10-14),
// then what the board's green arrow shows, best move after best move (the
// shortlist's live reading of each position, the scan's until it is in), so
// it works from any position, a line of one's own or an unscanned game
// included, and never contradicts the arrow. Pure: whether the board's
// position offers each and where the book ends; the panel does the playing.
import { moverOf, type ReviewState } from '@chess/api-review';

export interface MistakeAhead {
  /** The move's 1-based ply: the board shows the position before it. */
  ply: number;
  kind: 'blunder' | 'miss';
  san: string;
}

/**
 * The reader's blunder or miss played from the board's position, by the
 * scan's judgement, when the board shows the game itself (not a line, not a
 * position looked back at in a puzzle); undefined otherwise.
 */
export function mistakeAhead(state: ReviewState): MistakeAhead | undefined {
  const { game, scan, reader } = state;
  if (state.status !== 'ready' || !game || !scan || !reader || state.exploring || state.browse !== undefined) return undefined;
  const ply = state.ply + 1;
  const move = game.moves[state.ply];
  if (!move || moverOf(ply) !== reader) return undefined;
  const kind = scan.kind(ply, move, reader);
  return kind === 'blunder' || kind === 'miss' ? { ply, kind, san: move.san } : undefined;
}

/**
 * Whether Main line may be offered: a game is open and the board is not
 * looking back at a position in a puzzle. Any position: in the opening the
 * game's own book moves play first (`bookExitPly`), the arrow's after.
 */
export function mainLineAvailable(state: ReviewState): boolean {
  return state.status === 'ready' && !!state.game && state.browse === undefined;
}

/**
 * The ply where the game's book moves end: the last position the book
 * knows (the Opening tab's "Left the book at …"), the game's end when it
 * never left; undefined without a book reading. From a game position before
 * it, Main line steps the game itself up to it before playing the arrow's.
 */
export function bookExitPly(state: ReviewState): number | undefined {
  const last = state.book?.reading?.lastKnownPly;
  const total = state.game?.moves.length;
  if (last === undefined || total === undefined) return undefined;
  return Math.min(last, total);
}

/** How many plies Main line plays before it stops on its own: four moves each side, or a mate sooner. */
export const MAIN_LINE_PLIES = 8;

/**
 * The book move Main line plays at a position the book knows, where the
 * board shows theory rather than the engine (2026-10-14): the game's own
 * variation's move when the position is on its line (the board's green
 * "book" arrow), else the theory move heading to the opening the game was
 * named after (the same name, then the same family before the colon), else
 * the first listed, the book's main line. Undefined when theory is not
 * showing here.
 */
export function theoryMove(state: ReviewState, fen: string): string | undefined {
  const theory = state.theory;
  if (!theory?.active || theory.moves.length === 0) return undefined;
  const onLine = state.book?.variationMoveFrom(fen);
  if (onLine) return onLine;
  const name = state.book?.reading?.opening.name;
  if (name) {
    const same = theory.moves.find((m) => m.opening.name === name);
    if (same) return same.san;
    const family = name.split(':')[0];
    const near = theory.moves.find((m) => m.opening.name.split(':')[0] === family);
    if (near) return near.san;
  }
  return theory.moves[0].san;
}
